import {Component, ContentChild, forwardRef, Input, OnInit, TemplateRef} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NGXLogger} from 'ngx-logger';
import {Character} from 'gamesis-story-client';
import {Mode, selectActiveCharacters, selectActiveStoryCharacters, selectMode} from 'gamesis-state';
import {Store} from '@ngrx/store';
import {Observable, switchMap} from 'rxjs';

@Component({
    selector: 'character-picker',
    template: `
        @if (characters$ | async) {
            @if ((characters$ | async).length === 0) {
                <ion-text color="dark"
                          class="monospaced">There are no characters available
                </ion-text>
            } @else {
                @for (character of characters$ | async | sort: 'name'; track character.id) {
                    @if (character.id !== exclude) {
                        <character-chip [character]="character"
                                        (click)="toggle(character.id)"
                                        [outline]="!isSelected(character.id)"></character-chip>
                    }
                }
            }
        }
    `,
    styles: [`
        :host {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }
    `],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CharacterPicker),
        multi: true
    }],
    standalone: false
})
export class CharacterPicker implements OnInit, ControlValueAccessor {
    @ContentChild(TemplateRef, {static: true}) templ;

    public values: string[];

    public characters$: Observable<Character[]>;

    @Input()
    public exclude: string;

    constructor(private store: Store,
                private logger: NGXLogger) {
    }

    ngOnInit() {
        this.characters$ = this.store.select(selectMode).pipe(
            switchMap(mode => {
                if (mode === Mode.message) {
                    return this.store.select(selectActiveStoryCharacters);
                } else {
                    return this.store.select(selectActiveCharacters);
                }
            })
        );
    }

    writeValue(values: string[]) {
        if (values) {
            this.values = [...values];
        }
    }

    propagateChange = (_: any) => {
    }
    propagateTouch = () => {
    }

    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched(fn) {
        this.propagateTouch = fn;
    }

    isSelected(value: string): boolean {
        return this.values && this.values.some(val => val === value);
    }

    toggle(value: string) {
        if (!this.values) {
            this.values = [value];
        } else {
            if (this.isSelected(value)) {
                this.values = this.values.filter(val => val !== value);
            } else {
                this.values = [...this.values, value];
            }

            if (this.values.length === 0) {
                this.values = null;
            }
        }

        this.logger.log(`propagating changes ${this.values}`);
        this.propagateChange(this.values);
        this.propagateTouch();
    }
}
